import { Badge, Box, Button, Chip, Dialog, Divider, InputLabel, Stack, Typography } from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import { GlobalState } from "context/GlobalContext";
import { ReportsState } from "context/ReportsContext";
import CustomDateRangePicker from "components/Reports/ReusableComponents/CustomDateRangePicker/CustomDateRangePicker";
import {
	colorFilterCol,
	getSaleImFields,
	imFilterColumnsObj,
	lineGraphTypes,
	locationFilterColumns,
	storeFilterColumns,
	styleFilterCol,
	xAxisMonthDataCol,
} from "./data";
import { startOfDay, subDays, subYears, startOfYear, endOfYear, parseISO, endOfDay } from "date-fns";
import PersistantDrawer, { drawerWidth } from "components/Reports/ReusableComponents/PersistantDrawer/PersistantDrawer";
import { grey } from "@mui/material/colors";
import { getColumnValue, selectOptKeyName, inputTypes, isColumnDefault, arrType } from "data/globalData";
import { ChatRounded, FilterAltRounded } from "@mui/icons-material";
import SearchInputFieldWithDebounce from "components/Reports/ReusableComponents/SearchInputFieldWithDebounce/SearchInputFieldWithDebounce";

import RenderAttributes from "components/Reports/ReusableComponents/RenderAttributes/RenderAttributes";
import NotesSetion from "components/Reports/ReusableComponents/NotesSection/NotesSection";
import ZoomableContainer from "components/ReusableComponents/ZoomableContainer/ZoomableContainer";
import L1SaleRelatedCards from "./L1SaleRelatedCards/L1SaleRelatedCards";
import MonthWiseMetrics from "./MonthwiseBusinessMetrics/MonthwiseBusinessMetrics";
import CustomerVennDiagrams from "./CustomerVennDiagrams/CustomerVennDiagrams";
import DounutRelatedCards from "./DounutRelatedCards/DounutRelatedCards";
import MarimekkoRelatedCards from "./MarimekkoRelatedCards/MarimekkoRelatedCards";
import StoreListRelatedCards from "./StoreListCards/StoreListRelatedCards";
import moment from "moment";
import MrpRelatedCards from "./MrpRelatedCards/MrpRelatedCards";
import { COLOR_PALETTE_LIST } from "components/Reports/data";
import ChannelRelatedCards from "./ChannelRelatedCards/ChannelRelatedCards";

const getFYYear = () => {
    const today = new Date();
    // FY starts in April → month index 3
    return today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
};

const dateStaticRanges = [
	{
        label: "Last 3 Months",
        range: () => {
            const end = startOfDay(new Date());
            const start = startOfDay(subDays(end, 89));
            return { startDate: start, endDate: end };
        },
        isSelected: (range) => {
            const end = startOfDay(new Date()).getTime();
            const start = startOfDay(subDays(new Date(), 89)).getTime();
            return range.startDate.getTime() === start && range.endDate.getTime() === end;
        },
    },

    // ---------------- LAST 6 MONTHS ----------------
    {
        label: "Last 6 Months",
        range: () => {
            const end = startOfDay(new Date());
            const start = startOfDay(subDays(end, 179));
            return { startDate: start, endDate: end };
        },
        isSelected: (range) => {
            const end = startOfDay(new Date()).getTime();
            const start = startOfDay(subDays(new Date(), 179)).getTime();
            return range.startDate.getTime() === start && range.endDate.getTime() === end;
        },
    },

    // ---------------- THIS CALENDAR YEAR ----------------
    {
        label: "This Calendar Year",
        range: () => {
            const start = startOfYear(new Date());
            const end = endOfYear(new Date());
            return { startDate: start, endDate: end };
        },
        isSelected: (range) => {
            const start = startOfYear(new Date()).getTime();
            const end = endOfYear(new Date()).getTime();
            return range.startDate.getTime() === start && range.endDate.getTime() === end;
        },
    },
	{
		label: "This Financial Year",
		range: () => {
			const today = new Date();
			const year = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;

			const start = startOfDay(new Date(year, 3, 1));   // Apr 1
			const end = endOfDay(new Date(year + 1, 2, 31)); // Mar 31
			return { startDate: start, endDate: end };
		},
		isSelected: (range) => {
			const today = new Date();
			const year = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;

			const start = startOfDay(new Date(year, 3, 1)).getTime();
			const end = endOfDay(new Date(year + 1, 2, 31)).getTime();
			return range.startDate.getTime() === start && range.endDate.getTime() === end;
		},
	},

    // ---------------- LAST YEAR ----------------
    {
        label: "Last Year",
        range: () => {
            const start = startOfYear(subYears(new Date(), 1));
            const end = endOfYear(subYears(new Date(), 1));
            return { startDate: start, endDate: end };
        },
        isSelected: (range) => {
            const start = startOfYear(subYears(new Date(), 1)).getTime();
            const end = endOfYear(subYears(new Date(), 1)).getTime();
            return range.startDate.getTime() === start && range.endDate.getTime() === end;
        },
    },

    // ---------------- DEEWALI ----------------
    // {
    //     label: "Dewali",
    //     range: () => {
    //         const start = startOfDay(parseISO("2025-10-01"));
    //         const end   = startOfDay(parseISO("2025-11-30"));
    //         return { startDate: start, endDate: end };
    //     },
    //     isSelected: (range) => {
    //         const start = startOfDay(parseISO("2025-10-01")).getTime();
    //         const end   = startOfDay(parseISO("2025-11-30")).getTime();
    //         return range.startDate.getTime() === start && range.endDate.getTime() === end;
    //     },
    // },

    // ======================================================
    //                FINANCIAL YEAR QUARTERS
    // ======================================================

    // ---------------- THIS FY Q1 (APR 1 - JUN 30) ----------------
    {
        label: "This FY Q1",
        range: () => {
            const fy = getFYYear();
            const start = startOfDay(new Date(fy, 3, 1));   // April 1
            const end   = startOfDay(new Date(fy, 5, 30));  // June 30
            return { startDate: start, endDate: end };
        },
        isSelected: (range) => {
            const fy = getFYYear();
            const start = startOfDay(new Date(fy, 3, 1)).getTime();
            const end   = startOfDay(new Date(fy, 5, 30)).getTime();
            return range.startDate.getTime() === start && range.endDate.getTime() === end;
        },
    },

    // ---------------- THIS FY Q2 (JUL 1 - SEP 30) ----------------
    {
        label: "This FY Q2",
        range: () => {
            const fy = getFYYear();
            const start = startOfDay(new Date(fy, 6, 1));   // July 1
            const end   = startOfDay(new Date(fy, 8, 30));  // September 30
            return { startDate: start, endDate: end };
        },
        isSelected: (range) => {
            const fy = getFYYear();
            const start = startOfDay(new Date(fy, 6, 1)).getTime();
            const end   = startOfDay(new Date(fy, 8, 30)).getTime();
            return range.startDate.getTime() === start && range.endDate.getTime() === end;
        },
    },

    // ---------------- THIS FY Q3 (OCT 1 - DEC 31) ----------------
    {
        label: "This FY Q3",
        range: () => {
            const fy = getFYYear();
            const start = startOfDay(new Date(fy, 9, 1));   // Oct 1
            const end   = startOfDay(new Date(fy, 11, 31)); // Dec 31
            return { startDate: start, endDate: end };
        },
        isSelected: (range) => {
            const fy = getFYYear();
            const start = startOfDay(new Date(fy, 9, 1)).getTime();
            const end   = startOfDay(new Date(fy, 11, 31)).getTime();
            return range.startDate.getTime() === start && range.endDate.getTime() === end;
        },
    },

    // ---------------- THIS FY Q4 (JAN 1 - MAR 31) ----------------
    {
        label: "This FY Q4",
        range: () => {
            const fy = getFYYear();
            const start = startOfDay(new Date(fy + 1, 0, 1)); // Jan 1
            const end   = startOfDay(new Date(fy + 1, 2, 31)); // Mar 31
            return { startDate: start, endDate: end };
        },
        isSelected: (range) => {
            const fy = getFYYear();
            const start = startOfDay(new Date(fy + 1, 0, 1)).getTime();
            const end   = startOfDay(new Date(fy + 1, 2, 31)).getTime();
            return range.startDate.getTime() === start && range.endDate.getTime() === end;
        },
    },
];
const removeDropdownOptionsBasedonDependency = (column, data, options, allColumns) => {
	if ((column?.input_type === inputTypes.SELECT.key || column?.input_type === inputTypes.MULTIPLE_SELECT.key) && options) {
		let list = [];
		console.log(column, options, "columnsd.........");

		let dependentCols = [];
		if (column?.dependency)
			Object.keys(column?.dependency).forEach((colKey) => {
				dependentCols.push(allColumns.filter((row) => row.key === colKey)[0]);
			});
		console.log(dependentCols, column, "agfajfgdjfgujydfqjdhtg");
		if (dependentCols?.length) {
			column[selectOptKeyName]?.list?.forEach((attrVal) => {
				let push = true;
				dependentCols.forEach((col) => {
					// console.log("Checking column:", col.key, "Input Type:", col.input_type);
					// console.log("data[col.key]:", data[col.key]);
					if (data?.[col.key] && !isColumnDefault(col, data?.[col.key]))
						if (col.input_type === inputTypes.SELECT.key) {
							// console.log("Inside SELECT condition!");

							if (attrVal?.[col.key] === data?.[col.key]) {
							} else push = false;
						} else if (col.input_type === inputTypes.MULTIPLE_SELECT.key) {
							if (data?.[col.key]?.includes(attrVal?.[col.key])) {
							} else push = false;
						} else push = false;
				});
				if (push) list.push(attrVal);
			});
		} else list = column[selectOptKeyName];

		// console.log("kjdfnjlasjdn", list, column);
		if (Array.isArray(list))
			return {
				type: arrType,
				list: [...new Set(list?.map((val) => val?.[column.key]))].map((row) => {
					return { name: row, key: row };
				}),
			};
		else
			return {
				type: arrType,
				list: [...new Set(list?.list?.map((val) => val?.[column.key]))].map((row) => {
					return { name: row, key: row };
				}),
			};
	} else return {};
};
const Home = () => {
	const { setSnackBarOpen, setSnackBar, snackbar, setLoadingOpen } = GlobalState();
	const { reportLoading, setReportLoading, saleImDropdowns, setSaleImDropdowns } = ReportsState();

	// default date range with label to avoid CustomDateRangePicker crash
	// const defaultDateRange = {
	// 	...dateStaticRanges[0].range(),
	// 	key: "selection",
	// 	label: dateStaticRanges[0].label,
	// };
	const defaultDateRange = {
		start_date: new Date("2025-10-01"),
		end_date: new Date("2025-10-31")
	};

	const [filter, setFilter] = useState({});
	const [appliedFilterState, setAppliedFilterState] = useState({
		start_date: moment(defaultDateRange.start_date).format("YYYY-MM-DD"),
		end_date: moment(defaultDateRange.end_date).format("YYYY-MM-DD"),
	});

	const [dateRange, setDateRange] = useState(defaultDateRange);
	const [filterOpen, setFilterOpen] = useState(false);
	const [notesOpen, setNotesOpen] = useState(false);
	const [imFilterColumns, setImFilterColumns] = useState(Object.values(imFilterColumnsObj));
	const [appliedDateRange, setAppliedDateRange] = useState(defaultDateRange);
	const [pageData, setPageData] = useState([]);
	const { notifyCount, setNotifyCount, socket, setSessionData, setComments, allUsers } = ReportsState();

	const columns = [...imFilterColumns, ...storeFilterColumns, ...locationFilterColumns, styleFilterCol,colorFilterCol];
	const handleSelectedDate = (data) =>{
		setDateRange(data)
		let startDate = moment(data?.start_date).format("YYYY-MM-DD");
		let endDate = moment(data?.end_date).format("YYYY-MM-DD");
		setFilter({...filter, start_date: startDate, end_date: endDate})
	}

	const handleNotesButtonClick = () => {
		setNotesOpen(true);
		setNotifyCount(0);
	};
	const handleNotesCloseClick = () => {
		setNotesOpen(false);
	};

	// socket.onmessage = (event) => {
	// 	const data = JSON.parse(event.data);
	// 	// console.log("📩 called use effect message", event, data);
	// 	if (data?.action === "notify") {
	// 		// console.log("called use effect message 1");
	// 		!notesOpen && setNotifyCount((prev) => prev + 1);
	// 		data["name"] = allUsers.find((u) => u.email === data.sender_id)?.name || data.sender_id;
	// 		notesOpen && setComments((prev) => [...prev, data]);
	// 	}
	// 	if (data?.action === "session_create") {
	// 		setSessionData(data);
	// 	}
	// 	setLoadingOpen(false);
	// };

	useEffect(() => {
		if (saleImDropdowns) {
			const relColumns = imFilterColumns?.filter(Boolean)?.map((col) => {
				if (col?.input_type === inputTypes.SELECT.key || col?.input_type === inputTypes.MULTIPLE_SELECT.key)
					return {
						...col,
						// constraints: { ...col.constraints, NOTNULL: false },
						[selectOptKeyName]: {
							type: arrType,
							list: saleImDropdowns,
						},
						input_type: col?.input_type === inputTypes.SELECT.key ? inputTypes.MULTIPLE_SELECT.key : col?.input_type,
					};
				else return col;
			});
			setImFilterColumns((prev) => {
				return relColumns?.sort((a, b) => a?.sort_no - b?.sort_no);
			});
		}
	}, [saleImDropdowns]);

	const getDropdownFields = async () => {
		try {
			const res = await getSaleImFields();
			setSaleImDropdowns(res);
			return res;
		} catch (error) {
			console.log(error);
			setSnackBarOpen(true);
			setSnackBar({
				...snackbar,
				message: "Unable to fetch Im Dropdowns",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		}
		return [];
	};

	useEffect(() => {
		getDropdownFields();
	}, []);

	const fetchImDropdownDataBasedonColumn = (column) => {
		return async (offset = 0, limit = 20, searchTerm = "") => {
			const query = {
				limit,
				offset,
			};
			if (searchTerm) query["search"] = searchTerm;
			// construct your request logic
			const res = await axios.get(`/api/reports/get-im-col-dropdowns-by-search/${column.key}`, {
				params: query,
				query: {},
			});

			return res;
		};
	};
	const handleApplyFilterClick = () => {
		let startDate = moment(dateRange?.start_date).format("YYYY-MM-DD");
		let endDate = moment(dateRange?.end_date).format("YYYY-MM-DD");
		let obj = Object.keys(filter).reduce((acc, row) =>{
			if (Array.isArray(filter[row])  && !filter[row].length) return acc
			if (!filter[row]) return acc
			acc[row] =filter[row]
			return acc;
		},{})
		setAppliedFilterState({
			...obj,start_date: startDate, end_date: endDate
		});
	};

	const handleFilterSlider = () =>{
		if (filterOpen){
			setFilter(appliedFilterState)
			setDateRange({start_date: appliedFilterState?.start_date, end_date: appliedFilterState?.end_date})
		}
		setFilterOpen(!filterOpen)
	}

	return (
		<Box sx={{ display: "flex", 
		height: "calc(100vh - 140px)",
		overflow:"hidden",
		}}>
			{/* Persistent Drawer */}
			<PersistantDrawer
				drawerOpen={filterOpen}
				setDrawerOpen={handleFilterSlider}
				filter={filter}
				setFilter={setFilter}
				columns={columns} // pass your filter columns here
				anchor="left" // change to "right" if needed
				height="100%"
				displayApplyBtn={true}
				handleApplyClick={handleApplyFilterClick}
				sx={{ spacing: 2 }}>
				<Stack
					width="100%"
					spacing={1}
					// height="70px"
				>
					{/* <InputLabel sx={{ color: grey[600] }}>Date Range</InputLabel> */}
					<CustomDateRangePicker
						dateRange={dateRange}
						setDateRange={handleSelectedDate}
						// defaultDateRange={defaultDateRange}
						staticRanges={dateStaticRanges}
					/>
					{imFilterColumns ? (
						<Box display="flex" columnGap={"4%"} rowGap="10px" flexWrap="wrap" width="100%">
							<RenderAttributes
								columns={imFilterColumns ?? []}
								data={filter}
								setData={setFilter}
								errObj={{}}
								// setErrorObj={setErrorObj}
								allColumns={imFilterColumns}
								dependencyBasedOptFilterFun={removeDropdownOptionsBasedonDependency}
								selectOptObjName={selectOptKeyName}
								width="100%"
								longWidth="100%"
								disableLabel
							/>
						</Box>
					) : (
						<></>
					)}
					<SearchInputFieldWithDebounce
						column={styleFilterCol}
						data={filter}
						setData={setFilter}
						fetchData={fetchImDropdownDataBasedonColumn(styleFilterCol)}
						selectOptKeyName={selectOptKeyName}
						disableLabel
					/>
					<SearchInputFieldWithDebounce
						column={colorFilterCol}
						data={filter}
						setData={setFilter}
						fetchData={fetchImDropdownDataBasedonColumn(colorFilterCol)}
						selectOptKeyName={selectOptKeyName}
						disableLabel
					/>
				</Stack>
			</PersistantDrawer>

			{/* Main Content */}
			<Box
				component="main"
				sx={{
					flexGrow: 1,
					transition: (theme) =>
						theme.transitions.create("margin", {
							easing: theme.transitions.easing.sharp,
							duration: theme.transitions.duration.leavingScreen,
						}),
					ml: filterOpen ? `${drawerWidth}px` : 0, // auto push for left drawer
					p: 1,
					// overflow: "hidden",
					height: "100%",
					boxSizing: "border-box",
					position: "relative",
					width: "100%",
					overflow: "scroll",
				}}>
				{/* Top filter bar with CustomDateRangePicker */}
				<Box
					display="flex"
					flexWrap="wrap"
					alignItems="center"
					justifyContent="space-between"
					rowGap="5px"
					bgcolor="white"
					boxSizing="border-box"
					boxShadow={2}
					borderRadius="4px"
					p={1}
					zIndex={100}
					sx={{
						position: "absolute",
						...(filterOpen ? { width: "100px", top: "0px", right: "0%" } : { width: "100%", top: "0px", left: "0%" }),
						transition: (theme) =>
							theme.transitions.create("width", {
								easing: theme.transitions.easing.sharp,
								duration: theme.transitions.duration.leavingScreen,
							}),
					}}>
					{/* Filter button */}

					{!filterOpen && (
						<Box display="flex" overflow="hidden" columnGap="10px" width="calc(100% - 110px)">
							<Box>
								<Button
									onClick={handleFilterSlider}
									startIcon={<FilterAltRounded />}
									variant="contained"
									size="medium"
									sx={{ textTransform: "capitalize" }}>
									Filter
								</Button>
							</Box>

							<Box sx={{ display: "flex", columnGap: 1, flexWrap: "wrap", rowGap: "5px", alignItems: "center" }}>
								{/* <CustomDateRangePicker
									dateRange={dateRange}
									setDateRange={(newRange) => {
										setDateRange(newRange);
										handleApplyFilterClick();
									}}
									defaultDateRange={defaultDateRange}
									staticRanges={dateStaticRanges}
									maxDate={false}
								/> */}
								<Chip
									sx={{ textTransform: "capitalize" }}
									label={`${appliedFilterState?.start_date} - ${appliedFilterState?.end_date}`}
									size="small"
								/>
								{!Object.keys(appliedFilterState).length && (
									<Typography variant="body1" fontSize={16} color={grey[700]}>
										No Filters Applied
									</Typography>
								)}
								{columns
									?.filter((col) => appliedFilterState[col.key])
									.map((col) => (
										<Chip
											key={col.key}
											sx={{ textTransform: "capitalize" }}
											label={`${col.name.toLowerCase()} : ${getColumnValue(col, appliedFilterState)}`}
											size="small"
										/>
									))}
							</Box>
						</Box>
					)}
					<Badge badgeContent={notifyCount} color="secondary">
						<Button
							onClick={() => handleNotesButtonClick(true)}
							startIcon={<ChatRounded />}
							variant="contained"
							size="medium"
							sx={{ textTransform: "capitalize" }}>
							Notes
						</Button>
					</Badge>				
				</Box>
				{/* <ZoomableContainer */}
					{/* height="100%"
					sx={{ position: "static", border: `1px solid ${grey[100]} `, background: "inherit" }}
					controlsSx={{ position: "sticky", top: "70px", left: "100%", right: "0%" }}> */}
					<Box display="flex" columnGap={2} marginTop={"110px"} sx={{ zoom: 0.6}}>
						{/* <L1SaleRelatedCards filter={{ ...appliedFilterState, ...appliedDateRange }} /> */}
						<L1SaleRelatedCards filter={appliedFilterState} />
						<CustomerVennDiagrams filter={appliedFilterState} minWidth="40rem" height="48rem" />
						<Box
							sx={{
								// minWidth: "50rem",
								height: "48rem",
								background: "white",
							}}>
							<MonthWiseMetrics filter={appliedFilterState} width={1400} height={600} />
						</Box>
						<Box sx={{ zoom: 0.8}}>
							<MrpRelatedCards filter={appliedFilterState} width={900} height={960} color = {COLOR_PALETTE_LIST} />
						</Box>
					</Box>
					<Box sx={{ zoom: 0.5}} display={"flex"} margin={"20px 0px"}>
						<Box sx={{ zoom: 0.8}}>
							<ChannelRelatedCards filter={appliedFilterState} width={900} height={960} color = {COLOR_PALETTE_LIST} />
						</Box>
						{/* <Box display={"flex"} flexDirection={"column-reverse"} margin={"0px 15px"}>
							<SaleStoreRelatedCards filter={appliedFilterState}/>
						</Box> */}
						<Box display={"flex"} flexDirection={"column-reverse"} margin={"0px 15px"}>
							<StoreListRelatedCards filter={appliedFilterState}/>
						</Box>
					</Box>
					<Box sx={{ zoom: 0.5}}>
						<Typography sx={{fontSize:"var(--font-size-h1)"}} marginTop="20px" fontWeight="600">
							CATEGORY- DEEP DIVE
						</Typography>
						<Box display="flex" flexDirection="column" rowGap={2}>
							<MarimekkoRelatedCards filter={appliedFilterState} color = {COLOR_PALETTE_LIST} />
						</Box>
					</Box>

					{/* <img width="70%" style={{ paddingTop: "30px", paddingLeft: "30px" }} src="/images/reportHome.png" /> */}
				{/* </ZoomableContainer> */}
			</Box>
			<Dialog maxWidth="md" fullWidth open={notesOpen} onClose={handleNotesCloseClick}>
				<NotesSetion />
			</Dialog>
		</Box>
	);
};

export default Home;
